import { useCallback, useEffect, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import Button from '@mui/joy/Button'
import { CircularProgress } from '@mui/joy'
import { FiberManualRecord, Person, Refresh, Search as SearchIcon, Shield } from '@mui/icons-material'
import type { ProxyAssignedUser } from '@numeo/types'
import { proxyHealthApi } from './api'

const ease: [number, number, number, number] = [0.23, 1, 0.32, 1]
const PAGE_SIZE = 50

const AVATAR_COLORS = [
    'bg-blue-100 text-blue-700',
    'bg-violet-100 text-violet-700',
    'bg-emerald-100 text-emerald-700',
    'bg-amber-100 text-amber-700',
    'bg-rose-100 text-rose-700',
    'bg-cyan-100 text-cyan-700',
    'bg-indigo-100 text-indigo-700',
]

const getInitials = (value: string): string => {
    if (!value) return '?'
    const cleaned = value.split('@')[0]
    const parts = cleaned.split(/[._\s-]+/).filter(Boolean)
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
    return cleaned.substring(0, 2).toUpperCase()
}

const getAvatarColor = (seed: string): string => {
    let hash = 0
    for (let i = 0; i < seed.length; i++) hash = seed.charCodeAt(i) + ((hash << 5) - hash)
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

export default function VpnUsersPage() {
    const [users, setUsers] = useState<ProxyAssignedUser[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [search, setSearch] = useState('')

    const [counts, setCounts] = useState<{ count: number; activeCount: number } | null>(null)
    const [countsError, setCountsError] = useState(false)

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

    const loadUsers = useCallback(async (targetPage: number) => {
        setLoading(true)
        setError('')
        try {
            const data = await proxyHealthApi.listAssignedUsers(targetPage, PAGE_SIZE)
            setUsers(data.items)
            setTotal(data.pagination.total)
        } catch {
            setError('Failed to load VPN users')
        } finally {
            setLoading(false)
        }
    }, [])

    const loadCounts = useCallback(async () => {
        setCountsError(false)
        try {
            const data = await proxyHealthApi.countAssignedUsers()
            setCounts(data)
        } catch {
            setCountsError(true)
        }
    }, [])

    useEffect(() => {
        loadUsers(page)
    }, [page, loadUsers])

    useEffect(() => {
        loadCounts()
    }, [loadCounts])

    const filteredUsers = useMemo(() => {
        if (!search.trim()) return users
        const q = search.trim().toLowerCase()
        return users.filter((u) => (u.email ?? '').toLowerCase().includes(q) || u.userId.toLowerCase().includes(q))
    }, [users, search])

    const refresh = () => {
        loadUsers(page)
        loadCounts()
    }

    return (
        <div className="w-full p-6">
            {/* Breadcrumb */}
            <div className="mb-5 flex items-center gap-1.5 text-xs text-gray-400">
                <span>Home</span>
                <span>/</span>
                <span>Proxy</span>
                <span>/</span>
                <span className="font-medium text-gray-600">VPN Users</span>
            </div>

            {/* Page header */}
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, ease }}
                className="mb-6 flex items-center gap-3"
            >
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50">
                    <Shield sx={{ fontSize: 18 }} className="text-indigo-600" />
                </div>
                <div className="flex-1">
                    <h1 className="text-xl font-bold text-gray-900">VPN Users</h1>
                    <p className="text-sm text-gray-500">
                        Users who activated the egress proxy (VPN) within the last 24 hours, and who are using it right now.
                    </p>
                </div>
                <button
                    onClick={refresh}
                    className="flex h-9 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-50"
                >
                    <Refresh sx={{ fontSize: 16 }} />
                    Refresh
                </button>
            </motion.div>

            {/* Stat cards */}
            <div className="mb-6 grid grid-cols-2 gap-3">
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, ease }}>
                    <div className="rounded-xl border border-gray-100 border-t-[3px] border-t-indigo-400 bg-white p-4 shadow-sm">
                        <div className="mb-2 flex items-center justify-between">
                            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Recently active (24h)</p>
                            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-indigo-50">
                                <Person sx={{ fontSize: 14 }} className="text-indigo-600" />
                            </div>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{countsError ? '—' : (counts?.count ?? <CircularProgress size="sm" />)}</p>
                    </div>
                </motion.div>
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.05, ease }}>
                    <div className="rounded-xl border border-gray-100 border-t-[3px] border-t-emerald-400 bg-white p-4 shadow-sm">
                        <div className="mb-2 flex items-center justify-between">
                            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Active right now</p>
                            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-emerald-50">
                                <FiberManualRecord sx={{ fontSize: 12 }} className="text-emerald-600" />
                            </div>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{countsError ? '—' : (counts?.activeCount ?? <CircularProgress size="sm" />)}</p>
                    </div>
                </motion.div>
            </div>

            {/* List card */}
            <div className="rounded-xl border border-gray-100 bg-white shadow-sm">
                <div className="flex items-center gap-2 border-b border-gray-100 px-5 py-3">
                    <Person sx={{ fontSize: 16 }} className="text-gray-500" />
                    <p className="text-sm font-semibold text-gray-900">Users</p>
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">{total}</span>
                    <div className="ml-auto w-full max-w-xs">
                        <div className="relative">
                            <SearchIcon sx={{ fontSize: 14 }} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search this page…"
                                className="h-8 w-full rounded-lg border border-gray-200 bg-white pl-8 pr-3 text-sm focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                            />
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="border-b border-gray-100 bg-rose-50/50 px-5 py-3">
                        <p className="text-sm text-rose-700">{error}</p>
                    </div>
                )}

                {loading ? (
                    <div className="flex items-center justify-center py-16">
                        <CircularProgress size="sm" />
                    </div>
                ) : filteredUsers.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                        <Person sx={{ fontSize: 28 }} className="mb-2 text-gray-300" />
                        <p className="text-sm text-gray-500">{search ? 'No users match your search on this page' : 'No VPN users right now'}</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-gray-100 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                                    <th className="px-5 py-2.5">User</th>
                                    <th className="px-2 py-2.5">Recently active (24h)</th>
                                    <th className="px-2 py-2.5">Active now</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map((u, idx) => (
                                    <motion.tr
                                        key={u.userId}
                                        initial={{ opacity: 0, x: -8 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.2, delay: idx * 0.02, ease }}
                                        className="border-b border-gray-100 transition-colors duration-150 last:border-b-0 hover:bg-indigo-50/40"
                                    >
                                        <td className="px-5 py-3">
                                            <div className="flex items-center gap-2.5">
                                                <div
                                                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${getAvatarColor(u.email ?? u.userId)}`}
                                                >
                                                    {getInitials(u.email ?? u.userId)}
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-900">{u.email ?? <span className="text-gray-400">unknown email</span>}</p>
                                                    <p className="font-mono text-[11px] text-gray-400">{u.userId}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-2 py-3">
                                            {u.recentlyActive ? (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700 ring-1 ring-inset ring-indigo-200">
                                                    yes
                                                </span>
                                            ) : (
                                                <span className="text-[11px] text-gray-400">—</span>
                                            )}
                                        </td>
                                        <td className="px-2 py-3">
                                            {u.active ? (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">
                                                    <FiberManualRecord sx={{ fontSize: 8 }} />
                                                    active
                                                </span>
                                            ) : (
                                                <span className="text-[11px] text-gray-400">—</span>
                                            )}
                                        </td>
                                    </motion.tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-5 py-3">
                    <p className="text-xs text-gray-500">
                        Page {page} of {totalPages}
                    </p>
                    <div className="flex items-center gap-2">
                        <Button size="sm" variant="outlined" disabled={page === 1 || loading} onClick={() => setPage((p) => p - 1)}>
                            Previous
                        </Button>
                        <Button size="sm" variant="outlined" disabled={page === totalPages || loading} onClick={() => setPage((p) => p + 1)}>
                            Next
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    )
}
